import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DollarSign, Clock, Gift, Users, TrendingUp, RefreshCw, BellRing } from "lucide-react";
import api from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { StatusBadge } from "../../components/StatusBadge";
import { BlockedCheckouts } from "./BlockedCheckouts";

const money = (v) => `$${(v ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ label, value, sub, testid, accent }) {
  return (
    <div className={`border rounded-lg bg-white p-5 ${accent ? "border-[#B45309] bg-[#FEF3E2]" : "border-[#DDD7CF]"}`} data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F]">{label}</p>
      <p className="text-2xl font-medium text-[#1A1A1A] mt-1.5">{value}</p>
      {sub && <p className="text-xs text-[#6E675F] mt-1">{sub}</p>}
    </div>
  );
}

export const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = () => {
    setLoading(true);
    setRefreshKey((k) => k + 1);
    api.get("/admin/stats")
      .then(({ data }) => setStats(data))
      .catch(() => toast.error("Failed to load stats"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading || !stats) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { revenue, aov_30d, status_counts, pending_orders, points, top_products_30d, customers, demand_signals } = stats;

  return (
    <div data-testid="admin-stats-section">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Store Overview
        </p>
        <button onClick={load} data-testid="stats-refresh-button" className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A] transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-revenue-grid">
        <StatCard label="Revenue Today" value={money(revenue.today.revenue)} sub={`${revenue.today.orders} paid order${revenue.today.orders === 1 ? "" : "s"}`} testid="stats-revenue-today" />
        <StatCard label="Last 7 Days" value={money(revenue.week.revenue)} sub={`${revenue.week.orders} paid orders`} testid="stats-revenue-week" />
        <StatCard label="Last 30 Days" value={money(revenue.month.revenue)} sub={`${revenue.month.orders} paid orders`} testid="stats-revenue-month" />
        <StatCard label="All Time" value={money(revenue.all_time.revenue)} sub={`${revenue.all_time.orders} paid orders`} testid="stats-revenue-all" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Pending Orders" value={pending_orders} sub={pending_orders > 0 ? "awaiting e-Transfer match" : "all caught up"} testid="stats-pending-orders" accent={pending_orders > 0} />
        <StatCard label="Avg Order Value (30d)" value={money(aov_30d)} testid="stats-aov" />
        <StatCard label="Points Liability" value={money(points.liability)} sub={`${points.outstanding.toLocaleString()} points outstanding`} testid="stats-points-liability" />
        <StatCard label="Customers" value={customers.total} sub={`${customers.new_30d} new in 30d · ${customers.repeat_rate}% repeat buyers`} testid="stats-customers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="stats-top-products">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Top Products (30 days)
          </p>
          {top_products_30d.length === 0 ? (
            <p className="text-sm text-[#6E675F]" data-testid="stats-no-top-products">No paid orders in the last 30 days.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top_products_30d.map((p) => (
                  <TableRow key={`${p.name}-${p.dosage}`} data-testid={`stats-top-product-${p.name}`}>
                    <TableCell className="font-medium">{p.name} <span className="text-[#6E675F] font-normal">{p.dosage}</span></TableCell>
                    <TableCell className="text-right">{p.units}</TableCell>
                    <TableCell className="text-right font-medium">{money(p.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="stats-status-breakdown">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Orders by Status
          </p>
          <div className="space-y-3">
            {["pending", "verified", "shipped", "completed", "cancelled"].map((s) => (
              <div key={s} className="flex items-center justify-between" data-testid={`stats-status-${s}`}>
                <StatusBadge status={s} />
                <span className="font-medium text-[#1A1A1A]">{status_counts[s] || 0}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-[#DDD7CF] flex items-start gap-2 text-xs text-[#6E675F]">
            <Gift className="w-4 h-4 shrink-0 text-[#8C7B6B]" />
            <span>Points liability = outstanding points ÷ redeem rate ({points.redeem_rate} pts per $1). Budget for this as future discounts.</span>
          </div>
        </div>

        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="stats-demand-signals">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4 flex items-center gap-2">
            <BellRing className="w-4 h-4" /> Restock Demand
          </p>
          {!demand_signals || demand_signals.length === 0 ? (
            <p className="text-sm text-[#6E675F]" data-testid="stats-no-demand">No customers are currently waiting on out-of-stock products.</p>
          ) : (
            <>
              <p className="text-xs text-[#6E675F] mb-4">Customers waiting for a back-in-stock email — restock these first.</p>
              <div className="space-y-3">
                {demand_signals.map((d) => (
                  <div key={d.product_id} className="flex items-center justify-between gap-3" data-testid={`stats-demand-${d.product_id}`}>
                    <span className="text-sm font-medium text-[#1A1A1A] truncate">
                      {d.name} <span className="text-[#6E675F] font-normal">{d.dosage}</span>
                    </span>
                    <span className="text-xs font-semibold bg-[#FEF3E2] text-[#B45309] border border-[#B45309]/30 rounded-none px-3 py-1 whitespace-nowrap shrink-0">
                      {d.waiting} waiting
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mt-6">
        <BlockedCheckouts refreshKey={refreshKey} />
      </div>
    </div>
  );
};
